import arrayMutators from 'final-form-arrays';
import React, { useCallback, useMemo, useState } from 'react';
import { Form } from 'react-final-form';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router';

import { updateBabkaLoginOAuthAuth } from '../../../api';
import { DEFAULT_XSOLLA_ACCOUNT_VALUES } from '../../../constants';
import { useProjects } from '../../../contexts/settings';
import { usePaths } from '../../../routes/routesHelper';
import {
  CopyHiddenInput,
  FieldInput,
  FieldInputNumber,
  FieldTextarea,
  FormFooter,
  LinkFieldArray,
  Preloader
} from '../../ui';
import { FormInfoCard, LogoFormField, ScopesFormField } from '../SettingsForm';
import { addScope, deleteScope } from './mutators';
import { ModalContentBox, SignatureMethodChangeWrapper, TokenExtra, Wrapper } from './style';
import { getValidLogoUrl } from './utils';
import {
  clientDescriptionMaxLength,
  clientNameMaxLength,
  FormValues,
  Oauth2ModalValidator
} from './validator';

type Props = RouteComponentProps & {
  clientId: string;
};

const BabkaLoginClientSection = ({ history, clientId }: Props): JSX.Element => {
  const { dashboardPath } = usePaths();
  const intl = useIntl();

  const [fetching, setFetching] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { babkaLoginClient, setBabkaLoginClient } = useProjects();

  const client = useMemo(
    () => babkaLoginClient?.find((x) => x.id === Number(clientId)),
    [babkaLoginClient]
  );

  const handleBabkaLoginFormCancel = useCallback(() => {
    history.push({ pathname: dashboardPath() });
  }, []);

  const onSubmit = async (values: FormValues) => {
    if (client) {
      try {
        setFetching(true);
        setError(false);

        const { logoUrl, ...restFormValues } = {
          ...values,
          lifetime: Number(values.lifetime),
          partner_oauth2_page_url:
            client.partner_oauth2_page_url || DEFAULT_XSOLLA_ACCOUNT_VALUES.PARTNER_OAUTH2_PAGE_URL
        };

        await updateBabkaLoginOAuthAuth(Number(clientId), {
          ...restFormValues,
          icon: logoUrl,
          grant_types: client.grant_types
        });

        setBabkaLoginClient((prev) => {
          return prev.map((item) => {
            if (item.id === client.id) {
              return {
                ...item,
                ...restFormValues,
                icon_url: logoUrl
              };
            }
            return item;
          });
        });
      } catch (err) {
        setError(true);
      } finally {
        setFetching(false);
      }
    }
  };

  if (fetching || !client) {
    return <Preloader />;
  }

  return (
    <Form
      keepDirtyOnReinitialize
      onSubmit={onSubmit}
      initialValues={{
        logoUrl: getValidLogoUrl(client?.icon_url),
        name: client?.name,
        description: client?.description,
        redirect_uris: client?.redirect_uris || [],
        lifetime: client?.lifetime,
        scopes: client?.scopes || [],
        audience: client?.audience || []
      }}
      validate={Oauth2ModalValidator}
      mutators={{ ...arrayMutators, addScope, deleteScope }}>
      {({ form, handleSubmit, pristine, errors, values: { scopes } }) => {
        return (
          <form onSubmit={handleSubmit}>
            <SignatureMethodChangeWrapper>
              <Wrapper>
                <ModalContentBox>
                  <FormInfoCard />
                </ModalContentBox>
                <ModalContentBox>
                  <LogoFormField />
                </ModalContentBox>
                <ModalContentBox>
                  <FieldInput
                    label={intl.messages['authorization.settings.oauth2.table.client-name']}
                    name="name"
                    errorMessage={
                      errors?.name &&
                      intl.formatMessage(
                        {
                          id: errors?.name
                        },
                        {
                          number: clientNameMaxLength
                        }
                      )
                    }
                    placeholder={intl.formatMessage({
                      id: 'authorization.settings.oauth2.server.name.placeholder'
                    })}
                    data-test-id="xsolla-account-page-client-name"
                  />
                </ModalContentBox>
                <ModalContentBox>
                  <FieldTextarea
                    label={
                      intl.messages['authorization.settings.babkaLogin.field.description.label']
                    }
                    name="description"
                    errorMessage={
                      errors?.description &&
                      intl.formatMessage(
                        { id: errors?.description },
                        { number: clientDescriptionMaxLength }
                      )
                    }
                    resize
                    data-test-id="xsolla-account-page-client-description"
                  />
                </ModalContentBox>
                <ModalContentBox>
                  <CopyHiddenInput
                    type="text"
                    title={
                      intl.messages['authorization.settings.oauth2.modal.connect.success.client-id']
                    }
                    value={client?.id}
                    dataTestId="xsolla-account-page-client-id"
                  />
                </ModalContentBox>
                <ModalContentBox>
                  <CopyHiddenInput
                    title={intl.messages['authorization.settings.oauth2.server.secret-key.title']}
                    value={client?.secret}
                    dataTestId="xsolla-account-page-client-secret"
                  />
                </ModalContentBox>
                <ModalContentBox data-test-id="xsolla-account-page-redirect-urls-div">
                  <LinkFieldArray
                    name="redirect_uris"
                    label={intl.formatMessage({
                      id: 'authorization.settings.oauth2.modal.connect.redirect-uri.title'
                    })}
                    addLabel={intl.formatMessage({
                      id: 'configuration-page.urls.callback-urls.input.add-button'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'authorization.settings.oauth2.modal.connect.redirect-uri.placeholder'
                    })}
                    disabledValues={DEFAULT_XSOLLA_ACCOUNT_VALUES.REDIRECT_URIS}
                  />
                </ModalContentBox>
                <ModalContentBox data-test-id="xsolla-account-page-lifetime-div">
                  <FieldInputNumber
                    name="lifetime"
                    data-test-id="xsolla-account-page-lifetime"
                    hideArrows
                    subLabelCompact
                    label={intl.formatMessage({ id: 'settings.signature.token-lifetime.title' })}
                    errorMessage={
                      errors?.lifetime
                        ? intl.formatMessage(
                            { id: `${errors?.lifetime}` },
                            {
                              number:
                                `${errors?.lifetime}` === 'app.validation.value-more' ? 432000 : 300
                            }
                          )
                        : ''
                    }
                    subLabel={intl.formatMessage(
                      { id: 'settings.signature.token-lifetime.description-oauth' },
                      { br: () => <br /> }
                    )}
                    extra={() => (
                      <TokenExtra>
                        {intl.formatMessage({ id: 'settings.signature.token-lifetime.seconds' })}
                      </TokenExtra>
                    )}
                    extraAlign="right"
                  />
                </ModalContentBox>
                <ModalContentBox data-test-id="xsolla-account-page-scope-div">
                  <ScopesFormField
                    dataTestId="xsolla-account-page-scope"
                    scopes={scopes}
                    disabledScopes={DEFAULT_XSOLLA_ACCOUNT_VALUES.MANDATORY_SCOPES}
                    hiddenScopes={DEFAULT_XSOLLA_ACCOUNT_VALUES.HIDDEN_SCOPES}
                    onAdd={form.mutators.addScope}
                    onDelete={form.mutators.deleteScope}
                  />
                </ModalContentBox>
                <ModalContentBox data-test-id="xsolla-account-page-audience-div">
                  <LinkFieldArray
                    name="audience"
                    label={intl.formatMessage({
                      id: 'authorization.settings.babkaLogin.modal.connect.audience.title'
                    })}
                    addLabel={intl.formatMessage({
                      id: 'configuration-page.urls.callback-urls.input.add-button'
                    })}
                    placeholder={intl.formatMessage({
                      id: 'authorization.settings.oauth2.modal.connect.redirect-uri.placeholder'
                    })}
                  />
                </ModalContentBox>
              </Wrapper>
            </SignatureMethodChangeWrapper>

            <FormFooter
              onSubmit={() => onSubmit}
              onCancel={handleBabkaLoginFormCancel}
              fetching={fetching}
              error={error}
              disabled={pristine}
            />
          </form>
        );
      }}
    </Form>
  );
};

export default withRouter(BabkaLoginClientSection);
